import { BaseTable, Checkbox, Loading, NumberInput, PaginationData, TableHeaderSort, TableNumber, TableSorting } from "components";
import { FilterTypeSetStockByLocation } from "../types";
import { useList } from "../hooks";
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components";
import { convertNumber } from "utils";
import { Controller, useFormContext } from "react-hook-form";
import { FormSetStockType } from "../schemas/setStockByLocationSchemas";
import { CreateSetStock } from "./Create";
import { useStoreSelectedRows } from "../store";
import { CreateSetStockGlobal } from "../../SetStockGlobal/components/Create";

export function Table({
    filter,
    setFilter,
    data,
    isGlobal,
}: {
    filter: FilterTypeSetStockByLocation
    setFilter: React.Dispatch<React.SetStateAction<FilterTypeSetStockByLocation>>
    data: ReturnType<typeof useList>
    isGlobal?: Boolean
}) {

    // data
    const { items } = data.data

    // store
    const { selectedRows, toggleRow, toggleAll } = useStoreSelectedRows()
    const allChecked = items.length > 0 && items.every(item => selectedRows.some(r => Number(r.product_id) === Number(item.product_id)))

    // form
    const { control } = useFormContext<FormSetStockType>()

    const header: TableSorting[] = [
        { title: "SOH", sort: true, value: "soh" },
    ];

    const sortMapping: Record<string, string> = {
        "SOH": "soh",
    };

    const handleSort = (item: string, sortType: string) => {
        const sortParam = sortMapping[item]
            ? `${sortMapping[item]}:${sortType}`
            : "";
        setFilter({ ...filter, sort: sortParam });
    };


    return (
        <div>
            <div className="overflow-x-auto max-h-[calc(100vh-600px)]">
                <BaseTable className="border-collapse">
                    <thead className="sticky top-[200px] z-[3] !bg-black !text-white">
                        <tr>
                            <th>NO</th>
                            {!isGlobal && <th>LOCATION</th>}
                            <th>CATEGORY</th>
                            <th>PICTURE</th>
                            <th>BSI</th>
                            <th>BUILD</th>
                            <th>BARCODE</th>
                            <th>PRODUCT</th>
                            <th>UNIT(inventory)</th>
                            <TableHeaderSort
                                title={header}
                                onSort={(item, sortType) =>
                                    handleSort(item, sortType)
                                }
                            />
                            <th>MIN</th>
                            <th>MAX</th>
                            <th>
                                <Checkbox
                                    value={allChecked}
                                    onChange={e =>
                                        toggleAll(items as any, e.target.checked)
                                    }
                                />
                            </th>
                            <th>ACTION</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data.isLoading ? (
                            <tr>
                                <td colSpan={14} className="text-center">
                                    <Loading loading errorText="Loading..." />
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={14} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            items.map((item, index: number) => (
                                <tr key={item.product_id ?? index}>
                                    <td>
                                        {TableNumber({
                                            dataLength: items.length,
                                            pageCurrent: filter.page,
                                            index,
                                            limit: filter.limit,
                                            totalData: filter.total,
                                        })}
                                    </td>

                                    {!isGlobal && <td>{filter.location_name}</td>}

                                    <td>{!isGlobal ? item.mr_product_category_name : item.category_name}</td>

                                    <td className="!w-[80px]">
                                        <div className="flex justify-center">
                                            {Boolean(item?.pictures?.length) && (
                                                <PicturesSection
                                                    dataPicture={item.pictures}
                                                    items={1}
                                                    className="size-[50px] my-[-10px]"
                                                />
                                            )}
                                        </div>
                                    </td>

                                    <td className="text-center">
                                        {`${item.buy ? "B" : ""}${item.sell ? "S" : ""}${item.inventory ? "I" : ""}`}
                                    </td>

                                    <td className="text-center">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-primary"
                                            checked={item.build_item}
                                            disabled
                                        />
                                    </td>

                                    <td>{item.product_barcode}</td>
                                    <td>{item.product_name}</td>
                                    <td>{!isGlobal ? item.unit_convention_name : item.mr_unit_name}</td>

                                    <td className="text-right">
                                        {convertNumber(Number(item.soh) ?? 0).intoCurrency}
                                    </td>

                                    <td className="text-right">
                                        <Controller
                                            control={control}
                                            name={`items.${index}.min_qty`}
                                            render={({ field, fieldState }) => (
                                                <NumberInput
                                                    onValueChange={(v) => field.onChange(v.floatValue)}
                                                    value={field.value}
                                                    className="w-full"
                                                    error={fieldState.error?.message}
                                                    inputClass="text-right"
                                                />
                                            )}
                                        />
                                    </td>

                                    <td className="text-right">
                                        <Controller
                                            control={control}
                                            name={`items.${index}.max_qty`}
                                            render={({ field, fieldState }) => (
                                                <NumberInput
                                                    onValueChange={(v) => field.onChange(v.floatValue)}
                                                    value={field.value}
                                                    className="w-full"
                                                    error={fieldState.error?.message}
                                                    inputClass="text-right"
                                                />
                                            )}
                                        />
                                    </td>

                                    <td className="text-center">
                                        <Checkbox
                                            value={selectedRows.some(
                                                r => r.product_id === item.product_id
                                            )}
                                            onChange={e =>
                                                toggleRow(item as any, e.target.checked)
                                            }
                                        />
                                    </td>

                                    <td>
                                        {isGlobal ? (
                                            <CreateSetStockGlobal index={index} refecth={data.refetch} />
                                        ) : (
                                            <CreateSetStock index={index} refecth={data.refetch} />
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}

                    </tbody>
                </BaseTable>
            </div>
            <div className="mt-5">
                <PaginationData
                    data={data.data}
                    pagination={filter}
                    setPagination={setFilter}
                />
            </div>
        </div>
    )
}

